import React, { useState, useEffect, useCallback } from 'react';
import {
  getCommunications,
  createCommunication,
  updateCommunication,
  deleteCommunication,
  type CommunicationType,
  type CommunicationItem,
} from '../services/communications';
import { FileTextIcon, PlusIcon, TrashIcon } from './icons/AdminIcons';
import './ComunicacionesSection.css';

type SubTabType = 'newsletters' | 'personalizados' | 'redes_sociales';

const TYPE_TO_API: Record<SubTabType, CommunicationType> = {
  newsletters: 'newsletter',
  personalizados: 'draft',
  redes_sociales: 'social',
};

const LABELS: Record<SubTabType, { tab: string; add: string; empty: string; titlePlaceholder: string }> = {
  newsletters: {
    tab: 'Newsletters',
    add: 'Agregar hoja',
    empty: 'No hay hojas. Agregá una para empezar.',
    titlePlaceholder: 'Título de la hoja',
  },
  personalizados: {
    tab: 'Personalizados',
    add: 'Agregar borrador',
    empty: 'No hay borradores. Agregá uno para empezar.',
    titlePlaceholder: 'Título del borrador',
  },
  redes_sociales: {
    tab: 'Redes Sociales',
    add: 'Agregar hoja',
    empty: 'No hay hojas. Agregá una para empezar.',
    titlePlaceholder: 'Título de la hoja',
  },
};

interface ComunicacionesSectionProps {
  activeSubTab: SubTabType;
  isDarkMode?: boolean;
}

const ComunicacionesSection: React.FC<ComunicacionesSectionProps> = ({ activeSubTab, isDarkMode }) => {
  const apiType = TYPE_TO_API[activeSubTab];
  const labels = LABELS[activeSubTab];
  const [items, setItems] = useState<CommunicationItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCommunications(apiType);
      const list = data.items || [];
      setItems(list);
      setSelectedId(list.length > 0 ? list[0].id : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }, [apiType]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = items.find((i) => i.id === selectedId);

  useEffect(() => {
    if (selected) {
      setEditTitle(selected.title || '');
      setEditContent(selected.content || '');
    } else {
      setEditTitle('');
      setEditContent('');
    }
  }, [selected?.id, selected?.title, selected?.content]);

  const handleAdd = async () => {
    setSaving(true);
    setError(null);
    try {
      const { item } = await createCommunication(apiType, { title: '', content: '' });
      setItems((prev) => [...prev, item].sort((a, b) => a.sort_order - b.sort_order));
      setSelectedId(item.id);
      setEditTitle('');
      setEditContent('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (selectedId == null) return;
    setSaving(true);
    setError(null);
    try {
      const { item } = await updateCommunication(apiType, selectedId, { title: editTitle, content: editContent });
      setItems((prev) => prev.map((i) => (i.id === item.id ? item : i)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (selectedId == null || !window.confirm('¿Eliminar este elemento?')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCommunication(apiType, selectedId);
      setItems((prev) => prev.filter((i) => i.id !== selectedId));
      const next = items.find((i) => i.id !== selectedId);
      setSelectedId(next ? next.id : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="com-seccion com-seccion-loading">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className={`com-seccion ${isDarkMode ? 'com-seccion-dark' : ''}`}>
      <div className="com-toolbar">
        <button type="button" className="com-btn com-btn-primary" onClick={handleAdd} disabled={saving}>
          <PlusIcon width={18} height={18} /> {labels.add}
        </button>
      </div>
      {error && <div className="com-error">{error}</div>}
      <div className="com-layout">
        <div className="com-list">
          {items.length === 0 ? (
            <p className="com-empty">{labels.empty}</p>
          ) : (
            <ul className="com-list-ul">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`com-list-item ${selectedId === item.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <FileTextIcon width={18} height={18} />
                    <span className="com-list-title">{item.title || 'Sin título'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="com-editor">
          {selected ? (
            <>
              <div className="com-editor-header">
                <input
                  type="text"
                  className="com-input com-input-title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={handleSave}
                  placeholder={labels.titlePlaceholder}
                />
                <button type="button" className="com-btn com-btn-icon com-btn-danger" onClick={handleDelete} disabled={saving} title="Eliminar">
                  <TrashIcon width={18} height={18} />
                </button>
              </div>
              <div className="com-editor-body">
                <textarea
                  className="com-textarea"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  onBlur={handleSave}
                  placeholder="Contenido..."
                  rows={16}
                />
              </div>
              <div className="com-editor-footer">
                <button type="button" className="com-btn com-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </>
          ) : (
            <div className="com-editor-empty">
              <p>{items.length === 0 ? labels.empty : 'Seleccioná un elemento o agregá uno nuevo.'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComunicacionesSection;
export type { SubTabType };
